import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getSupabaseService } from '../lib/supabase-services';

// Query keys
export const taxEarningsKeys = {
  all: ['taxEarnings'] as const,
  byTaxYear: (taxYearStart: number) => [...taxEarningsKeys.all, taxYearStart] as const,
};

// Get tax earnings for a specific tax year (returns single object or null)
export const useTaxEarningsQuery = (taxYearStart: number) => {
  return useQuery({
    queryKey: taxEarningsKeys.byTaxYear(taxYearStart),
    queryFn: async () => {
      const service = getSupabaseService();
      return await service.getTaxEarningsByTaxYear(taxYearStart);
    },
  });
};

// Get all tax earnings
export const useAllTaxEarningsQuery = () => {
  return useQuery({
    queryKey: taxEarningsKeys.all,
    queryFn: async () => {
      const service = getSupabaseService();
      return await service.getAllTaxEarnings();
    },
  });
};

// Get monthly earnings from time entries (RPC function)
export const useGetMonthlyEarningsFromTimeEntries = () => {
  return useMutation({
    mutationFn: async (taxYearStart: number) => {
      const service = getSupabaseService();
      return await service.getMonthlyEarningsFromTimeEntries(taxYearStart);
    },
  });
};

// Upsert tax earnings for entire year mutation (JSONB format)
export const useUpsertTaxEarningsForYearMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      taxYearStart, 
      monthlyEarnings, 
      hasOtherJob, 
      otherJobSalary, 
      isPaye 
    }: { 
      taxYearStart: number; 
      monthlyEarnings: Record<number, number>;
      hasOtherJob?: boolean;
      otherJobSalary?: number;
      isPaye?: boolean;
    }) => {
      const service = getSupabaseService();
      return await service.upsertTaxEarningsForYear(taxYearStart, monthlyEarnings, hasOtherJob, otherJobSalary, isPaye);
    },
    onSuccess: (_, variables) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: taxEarningsKeys.byTaxYear(variables.taxYearStart) });
      queryClient.invalidateQueries({ queryKey: taxEarningsKeys.all });
    },
  });
};

